import { z } from 'zod';
import type { AISchemaField, UISchemaField } from '../types/index';

/**
 * カウントダウンセクション。
 *
 * - 期日 (targetDate, ISO8601) までの残り時間をリアルタイム更新で表示
 * - eyebrow / headline / CTA / logo は任意
 * - 終了後は expiredMessage を表示（または完了状態のメッセージ）
 *
 * 3 variants:
 *   thin_bar    — フル幅の細帯。トップ/ボトムスティッキー想定の inline 横並びレイアウト
 *   centered    — 中央寄せ。eyebrow + headline + 大型カウントダウン + CTA
 *   split       — 左右 50/50。左にテキスト + CTA、右にカウントダウン
 */
export const countdownAISchema: AISchemaField[] = [
  { name: 'eyebrow', type: 'string', required: false, max: 30 },
  { name: 'headline', type: 'string', required: false, max: 80, hint: '例: TimeWaver World 2026 開始まで' },
  {
    name: 'startDate',
    type: 'string',
    required: true,
    max: 40,
    hint: 'イベント開始日時 (ISO 8601 例: 2026-06-16T14:00:00+09:00)',
  },
  {
    name: 'endDate',
    type: 'string',
    required: false,
    max: 40,
    hint: 'イベント終了日時 (任意・指定すると「開催中→終了まで」のフェーズに自動切替)',
  },
  { name: 'startLabel', type: 'string', required: false, max: 40, hint: '開始前のラベル (省略時は「開催まで」)' },
  { name: 'duringLabel', type: 'string', required: false, max: 40, hint: '開催中のラベル (省略時は「終了まで」)' },
  { name: 'expiredMessage', type: 'string', required: false, max: 80, hint: '終了後の表示文 (省略時は「終了しました」)' },
  { name: 'primaryCta', type: 'string', required: false, max: 24 },
  { name: 'primaryCtaHref', type: 'url', required: false },
  { name: 'logoUrl', type: 'image_url', required: false },
  { name: 'description', type: 'string', required: false, max: 160, hint: 'centered/split variant で headline 下に表示' },
];

export const countdownUISchema: UISchemaField[] = [
  { name: 'eyebrow', type: 'text', label: 'Eyebrow', max: 30 },
  { name: 'headline', type: 'text', label: '見出し', max: 80 },
  { name: 'description', type: 'textarea', label: '説明文 (任意、centered / split で表示)', max: 160 },
  {
    name: 'startDate',
    type: 'datetime',
    label: 'イベント開始日時',
    placeholder: '2026-06-16T14:00',
    hint: '指定した日時まで「開催まで」のカウントダウンを表示',
  },
  {
    name: 'endDate',
    type: 'datetime',
    label: 'イベント終了日時 (任意)',
    placeholder: '2026-06-19T18:00',
    hint: '指定すると「開催中 → 終了まで」のフェーズに自動切替。空欄の場合、開始時刻に達したら「終了」表示',
  },
  { name: 'startLabel', type: 'text', label: '開始前のラベル (省略時は「開催まで」)', max: 40 },
  { name: 'duringLabel', type: 'text', label: '開催中のラベル (省略時は「終了まで」)', max: 40 },
  { name: 'expiredMessage', type: 'text', label: '終了後のメッセージ (省略時は「終了しました」)', max: 80 },
  { name: 'primaryCta', type: 'text', label: 'CTA ラベル (任意)', max: 24 },
  { name: 'primaryCtaHref', type: 'cta_url', label: 'CTA リンク' },
  { name: 'logoUrl', type: 'image', label: 'ロゴ画像 (任意)' },
];

export const countdownZodSchema = z.object({
  eyebrow: z.string().max(30).optional().nullable(),
  headline: z.string().max(80).optional().nullable(),
  description: z.string().max(160).optional().nullable(),
  // 開始日時: 必須 (legacy targetDate の場合も受け入れる)
  startDate: z.string().max(40).optional().nullable(),
  endDate: z.string().max(40).optional().nullable(),
  /** @deprecated 旧フィールド。新規は startDate を使う */
  targetDate: z.string().max(40).optional().nullable(),
  startLabel: z.string().max(40).optional().nullable(),
  duringLabel: z.string().max(40).optional().nullable(),
  expiredMessage: z.string().max(80).optional().nullable(),
  primaryCta: z.string().max(24).optional().nullable(),
  primaryCtaHref: z.string().optional().nullable(),
  logoUrl: z.string().optional().nullable(),
});

export type CountdownContent = z.infer<typeof countdownZodSchema>;

export const countdownSample: CountdownContent = {
  eyebrow: 'COUNTDOWN',
  headline: 'TimeWaver World 2026 開催まで',
  description: 'ベルリン本国で開催される TimeWaver の年に一度の祭典。早期登録枠は残りわずか。',
  startDate: '2026-06-16T14:00:00+09:00',
  endDate: '2026-06-19T18:00:00+09:00',
  targetDate: null,
  startLabel: '開催まで',
  duringLabel: '終了まで',
  expiredMessage: '受付は終了しました',
  primaryCta: '今すぐ登録する',
  primaryCtaHref: '#',
  logoUrl: null,
};
